import "./contextMenuItems.js";
import { describeObject } from "../../../base/common/describeObject.js";

import { Disposable } from "../../../base/common/lifecycle.js";
import { needleEngineTelemetryProps } from "../../../base/common/needleEngineRuntime.js";
import { CommandsRegistry } from "../../commands/common/commands.js";
import { isHierarchyNode } from "../../hierarchy/common/hierarchy.js";
import { ILogService } from "../../log/common/log.js";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage.js";
import { GetPropertyOptions, IProperty, IPropertyCollection, IPropertyProvider, IPropertyService, IPropertyStateProvider, isPropertyLike, iterateProperties, onPropertyChildrenMaterialized, PropertyAddress, PropertyChangedEvent, PropertyCollection, PropertyScope, PropertyViewMode } from "./properties.js";
import { CopyPropertyGroupCommand, SetPropertyCommand } from "./propertyCommands.js";
import { Emitter } from "../../../base/common/events.js";
import { IInstantiationService } from "../../instantiation/common/instantiation.js";
import { ICodeViewService } from "../../code/common/codeView.js";
import { collectCodeOptions } from "../../code/common/codeOptions.js";
import { IContextKeyService } from "../../contextkey/common/contextkey.js";
import { IEditEntitlementService } from "../../upsell/common/editEntitlement.js";



export class PropertyService extends Disposable implements IPropertyService {
    readonly _serviceBrand: undefined;

    private readonly _onPropertyChanged = this._register(new Emitter<PropertyChangedEvent>());
    readonly propertyChanged = this._onPropertyChanged.event;

    private readonly _providers: IPropertyProvider[] = [];

    constructor(
        @ILogService private readonly logger: ILogService,
        @IStorageService private readonly storageService: IStorageService,
        @IContextKeyService private readonly contextKeyService: IContextKeyService,
        @IEditEntitlementService private readonly entitlement: IEditEntitlementService,
        @IInstantiationService private readonly instantiationService: IInstantiationService,
    ) {
        super();
        this._register(CommandsRegistry.registerCommand(new SetPropertyCommand()));
        this._register(CommandsRegistry.registerCommand(new CopyPropertyGroupCommand()));
        // Properties created lazily (children materialized when a nested group
        // is first shown) also need their change events re-emitted from this
        // service — the subscribe loop in getProperties intentionally does not
        // force lazy children. Gate them here too so nested/expanded fields carry
        // the same login guard as the top-level ones.
        this._register(onPropertyChildrenMaterialized(children => {
            for (const prop of children) {
                prop.onDidChange(e => this._onPropertyChanged?.fire(e));
                this._maybeGateProperty(prop);
            }
        }));
    }

    registerProvider(provider: IPropertyProvider): void {
        if (this._providers.includes(provider)) {
            this.logger.debug("PropertyService: Provider already registered.", provider);
            return;
        }
        this.logger.debug("PropertyService: Registering provider");
        this._providers.push(provider);
    }

    unregisterProvider(provider: IPropertyProvider): void {
        const index = this._providers.indexOf(provider);
        if (index >= 0) {
            this.logger.debug("PropertyService: Unregistering provider");
            this._providers.splice(index, 1);
        }
    }


    private readonly _inflight: WeakMap<object, Promise<IPropertyCollection>> = new WeakMap();
    // private readonly _properties: WeakMap<object, IPropertyCollection> = new WeakMap();

    async getProperties(object: object, options: GetPropertyOptions): Promise<IPropertyCollection> {
        if (!object) {
            return PropertyCollection.Empty;
        }

        if (isPropertyLike(object)) {
            object = object.owner;
        }
        else if (isHierarchyNode(object)) {
            object = object.owner;
        }

        const active = this._inflight.get(object);
        // Inspector and Code View intentionally share concurrent reads.
        if (active) return active;

        // @TODO: we need to serialize the options too to be able to dispose
        // and then we put the pressure on disposing on this service but it should be done by whoever requests properties
        // We should not put more burden on the propertyservice i think (disposing requested collections)
        // if (this._properties.has(object)) {
        //     const prev = this._properties.get(object)!;
        //     prev.dispose();
        //     this._properties.delete(object);
        // }

        const fn: () => Promise<IPropertyCollection> = async () => {

            // const stateProvider = this._stateProviders[options.scope === PropertyScope.AI_QUERY ? "ai" : "default"];

            const col = new CollectedProperties();
            for (const provider of this._providers) {
                if (!provider.canHandle(object)) {
                    continue;
                }
                const providerProps = await provider.getProperties(object, {
                    ...options
                });
                if (providerProps) {
                    if (providerProps.name?.length && (!col.name || col.name.length < 3)) {
                        col.name = providerProps.name;
                    }
                    col.adopt(providerProps);
                }
            }
            if (!col.name?.length || col.name.length <= 2) {
                col.name = object.constructor?.name.length > 3 ? object.constructor.name : "";
                if ("name" in object && typeof object.name === "string") {
                    col.name = object.name;
                }
            }

            // Sort groups by priority
            for (const group of col.groups) {
                // console.log(group.properties)
                group.properties.sort((a, b) => {
                    const pa = a.metadata?.priority || 0;
                    const pb = b.metadata?.priority || 0;
                    return pb - pa;
                });
            }

            if (options.scope === PropertyScope.INSPECTOR) {
                col.persistFrom(this.storageService);
            }

            // Hook up property change events and re-emit from service.
            // Don't force lazy children here (this runs on every selection);
            // lazily materialized properties are hooked up via the
            // onPropertyChildrenMaterialized listener in the constructor.
            const gateEdits = options.scope === PropertyScope.INSPECTOR;
            for (const group of col.groups) {
                for (const prop of iterateProperties(group.properties, { materialize: false })) {
                    col.observe(prop, e => this._onPropertyChanged.fire(e));
                    if (gateEdits) this._maybeGateProperty(prop);
                }
            }


            if (options.scope === PropertyScope.INSPECTOR) {
                col.watch(this.contextKeyService, this.logger);
                // TEMPORARY soft coupling: push the code sources this walk already
                // produced (the `get-sourcecode` actions on each group) into the
                // code panel's service, so it doesn't re-enumerate the selection.
                // No hard dependency — a product without a code panel simply never
                // registers ICodeViewService and this is a no-op. Generalize to a
                // proper inspection adapter when there's a second consumer.
                this._reportCodeOptions(object, col);
            }

            return col;

        }
        const promise = fn().then(col => {
            // this._properties.set(object, col);
            return col;
        });
        this._inflight.set(object, promise);
        promise.finally(() => {
            this._inflight.delete(object);
        });
        return promise;
    }



    /** Soft-resolve the (optional) code view service and hand it the code options
     *  found in `col`. Silent no-op when the service isn't registered. */
    private _reportCodeOptions(object: object, col: IPropertyCollection): void {
        let codeView: ICodeViewService | undefined;
        try {
            codeView = this.instantiationService.invokeFunction(a => a.get(ICodeViewService));
        } catch {
            return; // no code panel in this product
        }
        // Same option builder the AI code_read tool uses — one menu, two consumers.
        codeView.reportOptions(object, collectCodeOptions(col.groups));
    }

    async findProperty(address: PropertyAddress): Promise<IProperty | null> {
        this.logger.debug("PropertyService: Finding property", address);
        for (const provider of this._providers) {
            try {
                const property = await provider.findProperty(address);
                if (property) {
                    return property;
                }
            }
            catch (e) {
                this.logger.error("PropertyService: Error finding property from provider", e, provider, address);
            }
        }
        return null;
    }


    /** Arm the edit gate on an editable inspector property when running against a
     *  local project. Reuses {@link IProperty.applyGuard} — the same hook the
     *  node-graph editor uses — so the veto is enforced in the value setter /
     *  `setValue` path. The identity/license/upsell dance itself lives in the
     *  shared {@link IEditEntitlementService} (one gate for every edit surface).
     *  No-op on public sites, on non-editable props, or when a provider already
     *  installed its own guard (e.g. the node graph). */
    private _maybeGateProperty(prop: IProperty): void {
        if (!this.entitlement.isGatedContext) return;
        if (prop.applyGuard) return;
        if (!prop.editable) return;
        prop.applyGuard = () => {
            const owner = (prop.owner as { constructor?: { name?: string } })?.constructor?.name;
            const detail = `edit ${prop.key}` + (owner ? ` on ${owner}` : "");
            return this.entitlement.guardEdit({
                source: "property_edit",
                detail,
                // historical event name — dashboards segment on it
                analyticsEvent: "property.edit.license_required",
                analyticsProps: needleEngineTelemetryProps(),
                subtitle: "Live-edit materials, lights and transforms on your own projects.",
                message: "Editing on local projects requires Needle Inspector Pro (included with Needle Engine Pro). Sign in with a free account to continue.",
            });
        };
    }


}


export class PropertyStateProviderGroup implements IPropertyStateProvider {

    private readonly providers: IPropertyStateProvider[] = [];

    constructor(
        ...providers: Array<IPropertyStateProvider | null | undefined>
    ) {
        this.providers = providers.filter(p => p != null) as IPropertyStateProvider[];
    }

    isHidden(object: object, key: string): boolean {
        for (const provider of this.providers) {
            if (provider.isHidden(object, key)) {
                return true;
            }
        }
        return false;
    }

    setHidden(object: object, key: string, hidden: boolean): void {
        for (const provider of this.providers) {
            provider.setHidden?.(object, key, hidden);
        }
    }
}


export class BasePropertyStateProvider extends Disposable implements IPropertyStateProvider {

    constructor(
        protected readonly storageService: IStorageService,
    ) {
        super();
    }

    isHidden(object: object, key: string): boolean {
        const storageKey = `property.${this.tryGetPropertyStorageKey(object, key)}.hidden`;
        return this.storageService.getBoolean(storageKey, StorageScope.APPLICATION, false);
    }
    setHidden(object: object, key: string, hidden: boolean): void {
        const storageKey = `property.${this.tryGetPropertyStorageKey(object, key)}.hidden`;
        this.storageService.store(storageKey, hidden, StorageScope.APPLICATION, StorageTarget.MACHINE);
    }

    protected tryGetPropertyStorageKey(object: object, propertyKey: string) {
        let objectId: string | null = null;
        if ("type" in object) {
            objectId = (object as any).type;
        }
        else if (object.constructor && object.constructor.name) {
            objectId = object.constructor.name;
        }
        else {
            objectId = "object";
        }
        return `${objectId}.${propertyKey}`;
    }
}

export class ViewModePropertyStateProvider extends BasePropertyStateProvider implements IPropertyStateProvider {

    constructor(
        private readonly viewMode: PropertyViewMode,
        storageService: IStorageService,
    ) {
        super(storageService);
    }

    protected tryGetPropertyStorageKey(object: object, propertyKey: string): string {
        let key = super.tryGetPropertyStorageKey(object, propertyKey);
        const viewModeName = PropertyViewMode[this.viewMode];
        key = `${key}.viewmode.${viewModeName}`;
        return key;
    }
}

/** @deprecated use {@link describeObject} — kept as the name the recursion
 *  diagnostic and its tests already use. */
export const describeRecursionTarget = describeObject;

class CollectedProperties extends PropertyCollection {
    adopt(collection: IPropertyCollection): void {
        if (collection !== PropertyCollection.Empty) this._register(collection);
        this.groups.push(...collection.groups);
    }
    observe(property: IProperty, listener: (event: PropertyChangedEvent) => void): void {
        this._register(property.onDidChange(listener));
    }
}
